import { ApiClient } from '@listening/api-contract/api-client-tag';
import { listeningApiChapterDtoToDocReader } from '@listening/api-contract/legacy-client-types';
import { getNotePlaybackTarget } from '@listening/frontend/audio-chunk-player/note-playback';
import { useFilteredNotesQuery } from '@listening/frontend/query/notes-collection';
import { parsePublicIdFromSlug } from '@listening/utils/slug-utils';
import { Effect } from 'effect';
import { useEffect, useRef } from 'react';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
} from '@/lib/audio-chunk-player';
import { getAudioFromAnyCache } from '@listening/frontend/query/audio-list';
import { useQueryClient } from '@tanstack/react-query';
import type { FetchChaptersFn } from '@/lib/doc-reader';
import { runApiPromise } from '@/runtimes/effect-query';

const slugToAudioId = parsePublicIdFromSlug;

export const fetchChaptersFn: FetchChaptersFn = async (opts) => {
  const data = await runApiPromise(
    Effect.flatMap(ApiClient, (api) =>
      api.Audio.ListChapters({
        params: { audioOrSlug: opts.audioId },
        query: { cursor: opts.cursor, limit: opts.limit },
      }),
    ),
  );
  return listeningApiChapterDtoToDocReader(data);
};

function useNotePlaybackTarget(noteId: string | undefined) {
  // Only subscribed while the route names a note: this page also serves public
  // readers, where the notes request is unauthorized — and without a note the
  // collection isn't needed at all.
  const notes = useFilteredNotesQuery({ enabled: noteId !== undefined }).data;
  if (noteId === undefined) return undefined;
  const note = notes.find((candidate) => candidate.id === noteId);
  return note ? getNotePlaybackTarget(note) : undefined;
}

/**
 * Loads the document this route names, at the note in `?note=<id>` when there
 * is one.
 *
 * The note travels with the load rather than seeking after it: a seek needs a
 * document the player has already resolved, and the load that resolves it is
 * still in flight while the reader mounts. A note that resolves late issues its
 * own load, which supersedes the first; one that never resolves — deleted,
 * unreadable, or anchorless — leaves the document at its saved progress.
 */
export function useLoadAudioFromSlug(slug: string, noteId?: string) {
  const slugAudioId = slugToAudioId(slug);
  const audioPlayerId = useAudioChunkSelector((s) => s.audioId);
  const lookupAudioId = useAudioChunkSelector((s) => s.lookupAudioId);

  const audioChunkPlayer = useAudioChunkPlayer();
  const { loadAudio, loadAudioAtNote, loadAudioFromProgress, unloadAudio } =
    audioChunkPlayer;
  const queryClient = useQueryClient();
  const noteTarget = useNotePlaybackTarget(noteId);

  const isAlreadyLoaded =
    slugAudioId === audioPlayerId || slugAudioId === lookupAudioId;
  // One load per document and one per (document, note): a load is latest-wins
  // and takes a while to reach the store, so a re-render in between must not
  // issue it twice.
  const noteRequest =
    noteId === undefined ? undefined : `${slugAudioId}|${noteId}`;
  const loadedDocument = useRef<string | undefined>(undefined);
  const loadedNote = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (noteTarget && loadedNote.current !== noteRequest) {
      loadedNote.current = noteRequest;
      loadedDocument.current = slugAudioId;
      if (!isAlreadyLoaded && audioPlayerId) unloadAudio();
      loadAudioAtNote(slugAudioId, noteTarget);
      return;
    }
    if (isAlreadyLoaded || loadedDocument.current === slugAudioId) return;
    loadedDocument.current = slugAudioId;
    if (audioPlayerId) unloadAudio();

    // Look for the row in any audio cache (bounded lists + per-id detail/summary)
    // to seed playback from progress; fall back to a fresh load on a miss.
    const cached = getAudioFromAnyCache(queryClient, slugAudioId);
    if (cached) {
      loadAudioFromProgress(cached);
    } else {
      loadAudio(slugAudioId);
    }
  }, [
    audioPlayerId,
    isAlreadyLoaded,
    loadAudio,
    loadAudioAtNote,
    loadAudioFromProgress,
    noteRequest,
    noteTarget,
    queryClient,
    slugAudioId,
    unloadAudio,
  ]);
}
